import {AbstractApplicationFlowComponent} from '../application-flow/abstract-application-flow.component';
import {Component, OnInit, Type} from '@angular/core';
import {Step} from '../application-flow/steps/step';
import {LivelihoodInformationStep} from '../application-flow/steps/livelihood-information.step';
import {ProfessionInteractor} from '../interactors/profession.interactor';
import {LoanApplicationFlowNavigator} from '../application-flow/loan-application-flow.navigator';
import {ProfessionType} from '../model/profession-type.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {IndustryInteractor} from '../interactors/industry.interactor';
import {EmployeeType} from '../model/employee-type.model';
import {LoanApplicationInteractor} from '../interactors/loan-application.interactor';
import {LivelihoodInformation} from '../model/livelihood-information.model';
import {LivelihoodInformationInteractor} from '../interactors/livelihood-information.interactor';
import {Logger} from '../utils/Logger';
import {MatDialog} from '@angular/material';
import {SalarycheckComponent} from '../salarycheck/salarycheck.component';
import {isUndefined} from 'lodash';
import * as moment from 'moment';
import {ActivatedRoute} from '@angular/router';
import {Income, IncomeType} from '../model/eligibility-check/income.model';
import {AutoCompleteValidators} from '../utils/AutoCompleteValidators';

@Component({
    templateUrl: 'livelihood-information.component.html',
    styleUrls: ['../../assets/styles/finzy-styles.scss', '../loan-application-flow-component/application-flow.css', 'livelihood-information.component.css']
})
export class LivelihoodInformationComponent extends AbstractApplicationFlowComponent implements OnInit {
    public selfEmployedProfessions = [];
    public professionalProfessions = [];
    public industries = [];
    public completeSelfEmployedProfessions = [];
    public completeProfessionalProfessions = [];
    public completeIndustries = [];
    public salariedFormGroup: FormGroup;
    public selfEmployedFormGroup: FormGroup;
    public professionalFormGroup: FormGroup;
    public EmployeeType = EmployeeType;
    public selectEmployeeType: EmployeeType = EmployeeType.Salaried;
    public livelihoodInformation: LivelihoodInformation;
    public readonly EmploymentOptions = [
        {displayName: 'Salaried', value: EmployeeType.Salaried},
        {displayName: 'Self Employed', value: EmployeeType.SelfEmployed},
        {displayName: 'Professional', value: EmployeeType.Professional},
    ];
    private shouldShowLoader = false;
    private amountOfExperienceError: string;

    constructor(loanApplicationFlowNavigator: LoanApplicationFlowNavigator, private professionInteractor: ProfessionInteractor, formBuilder: FormBuilder,
                private industryInteractor: IndustryInteractor, private loanApplicationInteractor: LoanApplicationInteractor,
                private livelihoodInformationInteractor: LivelihoodInformationInteractor, private dialog: MatDialog, private activatedRoute: ActivatedRoute) {
        super(loanApplicationFlowNavigator);
        this.salariedFormGroup = formBuilder.group({
            industry: formBuilder.control(null, [Validators.required]),
            company: formBuilder.control(null, [Validators.required]),
            totalExperienceYear: formBuilder.control(null, [Validators.required]),
            totalExperienceMonth: formBuilder.control(null, [Validators.max(11)]),
            income: formBuilder.control(null, [Validators.required])
        });
        this.selfEmployedFormGroup = formBuilder.group({
            profession: formBuilder.control(null, [Validators.required]),
            industry: formBuilder.control(null, [Validators.required]),
            company: formBuilder.control(null, [Validators.required]),
            totalExperienceYear: formBuilder.control(null, [Validators.required]),
            totalExperienceMonth: formBuilder.control(null, [Validators.max(11)]),
            income: formBuilder.control(null, [Validators.required])
        });
        this.professionalFormGroup = formBuilder.group({
            profession: formBuilder.control(null, [Validators.required]),
            company: formBuilder.control(null, [Validators.required]),
            totalExperienceYear: formBuilder.control(null, [Validators.required]),
            totalExperienceMonth: formBuilder.control(null, [Validators.max(11)]),
            income: formBuilder.control(null, [Validators.required])
        });

        activatedRoute.data.subscribe(
            ({income}: { income: Income }) => {
                if (income) {
                    if (income.type === IncomeType.Monthly) {
                        this.selectEmployeeType = EmployeeType.Salaried;
                        this.salariedFormGroup.patchValue(
                            {
                                income: income.amount
                            });
                    } else {
                        this.selectEmployeeType = EmployeeType.SelfEmployed;
                        this.selfEmployedFormGroup.patchValue(
                            {
                                income: income.amount
                            });
                    }
                }
            }
        );
    }

    step(): Type<Step> {
        return LivelihoodInformationStep;
    }

    async ngOnInit(): Promise<void> {
        const selfEmployedProfessions = await this.professionInteractor.getByType(ProfessionType.SelfEmployed);
        const professionalProfessions = await this.professionInteractor.getByType(ProfessionType.Professional);
        selfEmployedProfessions
            .forEach(profession => {
                this.completeSelfEmployedProfessions.push(profession.name);
                this.selfEmployedProfessions.push(profession.name);
            });
        professionalProfessions
            .forEach(profession => {
                this.professionalProfessions.push(profession.name);
                this.completeProfessionalProfessions.push(profession.name);
            });
        this.completeIndustries = this.industries = await this.industryInteractor.getAll();
        this.setDropDownValidators();
        await this.prefillInformation();
    }

    public setDropDownValidators() {
        this.salariedFormGroup.controls.industry.setValidators(AutoCompleteValidators.valueSelected(this.industries));
        this.selfEmployedFormGroup.controls.industry.setValidators(AutoCompleteValidators.valueSelected(this.industries));
        this.selfEmployedFormGroup.controls.profession.setValidators(AutoCompleteValidators.valueSelected(this.selfEmployedProfessions));
        this.professionalFormGroup.controls.profession.setValidators(AutoCompleteValidators.valueSelected(this.professionalProfessions));
    }

    filterIndustryList(enteredIndustryText: string) {
        this.industries = this.completeIndustries.filter(industry => industry.toUpperCase().includes(enteredIndustryText.toUpperCase()));
    }

    filterSelfEmployedProfessionsList(enteredText: string) {
        this.selfEmployedProfessions = this.completeSelfEmployedProfessions.filter(profession => profession.toUpperCase().includes(enteredText.toUpperCase()));
    }

    filterProfessionalProfessionsList(enteredText: string) {
        this.professionalProfessions = this.completeProfessionalProfessions.filter(profession => profession.toUpperCase().includes(enteredText.toUpperCase()));
    }

    onEmploymentTypeChange() {
        this.industries = this.completeIndustries;
        this.selfEmployedProfessions = this.completeSelfEmployedProfessions;
        this.professionalProfessions = this.completeProfessionalProfessions;
        this.amountOfExperienceError = null;
    }

    private showDialogIfSalaryLessThanRequiredAmount(formData: FormGroup): boolean {
        const income = formData.controls.income.value;
        let requiredIncome = 0;
        if (this.selectEmployeeType === EmployeeType.Salaried) {
            requiredIncome = 25000;
        } else if (this.selectEmployeeType === EmployeeType.SelfEmployed) {
            requiredIncome = 500000;
        } else {
            requiredIncome = 300000;
        }
        if (income < requiredIncome) {
            this.dialog.open(SalarycheckComponent,
                {
                    data: requiredIncome.toString(),
                    height: '250px', width: '550px'
                });

            return true;
        }

        return false;
    }

    async onSubmit(formGroup: FormGroup) {
        this.amountOfExperienceError = null;
        if (formGroup.invalid) {

            Logger.log(`Form is not valid. Not going ahead with the submission.`);

            return;
        }

        if (this.selectEmployeeType === EmployeeType.Salaried) {
            if (!this.isTotalExperienceValid(formGroup.controls.totalExperienceYear.value, formGroup.controls.totalExperienceMonth.value)) {
                return;
            }
        } else if (this.selectEmployeeType === EmployeeType.Professional || this.selectEmployeeType === EmployeeType.SelfEmployed) {
            if (!this.isYearsOfExperienceValid(formGroup.controls.totalExperienceYear.value, 1)) {
                return;
            }
        }

        if (this.showDialogIfSalaryLessThanRequiredAmount(formGroup)) {
            return;
        }

        const controls = formGroup.controls;

        this.showLoader();
        try {
            const loanApplicationId = (await this.loanApplicationInteractor.get()).id;

            const selectEmployeeType = this.selectEmployeeType;
            const company = controls.company.value;
            const industry = selectEmployeeType === EmployeeType.Professional ? null : controls.industry.value;
            const totalExpInMonth = controls.totalExperienceYear.value * 12 + controls.totalExperienceMonth.value;
            const profession = selectEmployeeType === EmployeeType.Salaried ? null : controls.profession.value;
            const income = controls.income.value;

            const updatedLivelihoodInformation = new LivelihoodInformation(
                loanApplicationId,
                industry,
                company,
                totalExpInMonth,
                selectEmployeeType,
                profession,
                income
            );

            updatedLivelihoodInformation.id = this.livelihoodInformation ? this.livelihoodInformation.id : null;
            updatedLivelihoodInformation.version = this.livelihoodInformation ? this.livelihoodInformation.version : null;

            this.livelihoodInformation =
                await this.livelihoodInformationInteractor.createOrUpdateLivelihoodInformation(updatedLivelihoodInformation);
            await this.nextPage();
        } finally {
            this.hideLoader();
        }
    }

    async prefillInformation() {
        const loanApplicationId = (await this.loanApplicationInteractor.get()).id;
        this.livelihoodInformation = await this.livelihoodInformationInteractor.getByLoanApplicationId(loanApplicationId);
        if (this.livelihoodInformation) {
            this.selectEmployeeType = this.livelihoodInformation.employeeType;
            switch (this.livelihoodInformation.employeeType) {
                case EmployeeType.Salaried:
                    this.setSalariedForm(this.salariedFormGroup, this.livelihoodInformation);
                    break;
                case EmployeeType.SelfEmployed:
                    this.setSelfEmployedForm(this.selfEmployedFormGroup, this.livelihoodInformation);
                    break;
                case EmployeeType.Professional:
                    this.setProfessionalForm(this.professionalFormGroup, this.livelihoodInformation);
            }
        }
    }

    public isYearsOfExperienceValid(yearsOfExperience: number, minYearsOfExperience = 0): boolean {
        if (isUndefined(yearsOfExperience) || yearsOfExperience < minYearsOfExperience) {
            this.amountOfExperienceError = `Minimum years of experience for eligibilty is ${minYearsOfExperience} year(s).`;
            return false;
        } else {
            this.amountOfExperienceError = null;
            return true;
        }
    }

    protected setSalariedForm(formGroup: FormGroup, livelihoodInformation: LivelihoodInformation) {
        formGroup.patchValue({
            industry: livelihoodInformation.industry,
            company: livelihoodInformation.company,
            totalExperienceYear: Math.floor(livelihoodInformation.totalExperienceMonths / 12),
            totalExperienceMonth: livelihoodInformation.totalExperienceMonths % 12,
            income: livelihoodInformation.income
        });
    }

    protected setSelfEmployedForm(formGroup: FormGroup, livelihoodInformation: LivelihoodInformation) {
        formGroup.patchValue({
            industry: livelihoodInformation.industry,
            profession: livelihoodInformation.profession,
            company: livelihoodInformation.company,
            totalExperienceYear: Math.floor(livelihoodInformation.totalExperienceMonths / 12),
            totalExperienceMonth: livelihoodInformation.totalExperienceMonths % 12,
            income: livelihoodInformation.income
        });
    }

    protected setProfessionalForm(formGroup: FormGroup, livelihoodInformation: LivelihoodInformation) {
        formGroup.patchValue({
            industry: livelihoodInformation.industry,
            company: livelihoodInformation.company,
            profession: livelihoodInformation.profession,
            totalExperienceYear: Math.floor(livelihoodInformation.totalExperienceMonths / 12),
            totalExperienceMonth: livelihoodInformation.totalExperienceMonths % 12,
            income: livelihoodInformation.income
        });
    }

    protected errorMessages(): object {
        return {
            industry: [{
                type: 'required',
                message: 'Please select the industry type'
            }, {
                type: 'match',
                message: 'Please select a valid option'
            }],
            company: [{
                type: 'required',
                message: 'Please enter the name of the company / firm'
            }],
            totalExperienceYear: [{
                type: 'required',
                message: 'Please enter total years'
            }],
            totalExperienceMonth: [{
                type: 'max',
                message: 'Please enter valid number of months'
            }],
            profession: [{
                type: 'required',
                message: 'Please select profession'
            }, {
                type: 'match',
                message: 'Please select a valid option'
            }],
            income: [{
                type: 'required',
                message: 'Please enter income'
            }, {
                type: 'min',
                message: 'Please enter your experience in months'
            }]
        };
    }

    private showLoader() {
        this.shouldShowLoader = true;
    }

    private hideLoader() {
        this.shouldShowLoader = false;
    }

    private isTotalExperienceValid(yearsOfExperience: number, monthsOfExperience: number) {
        const totalMonthsOfExperience = moment.duration(yearsOfExperience, 'years').add(monthsOfExperience, 'months').asMonths();
        if (totalMonthsOfExperience >= 6) {
            return true;
        } else {
            this.amountOfExperienceError = 'The minimum experience at which you can apply for a loan is 6 months.';
            return false;
        }
    }
}
